import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  createCell,
  createSelectionState,
  updateRange,
  isSelected,
  getSelectedCells,
  copyToClipboard,
  parseClipboardData
} from "../utils/selectionEngine";

function WordTableEditor() {
  const [tableData, setTableData] = useState(() => {
    const init = {};
    for (let r = 0; r < 5; r++) {
      for (let c = 0; c < 4; c++) {
        init[`${r}-${c}`] = '';
      }
    }
    return init;
  });
  const [rows, setRows] = useState(5);
  const [cols, setCols] = useState(4);
  const [selection, setSelection] = useState(createSelectionState);
  const [activeCell, setActiveCell] = useState(null);
  const selectingRef = useRef(false);
  const selectionRef = useRef(selection);
  selectionRef.current = selection;
  const tableRef = useRef(null);
  const cellRefs = useRef({});

  const getCell = useCallback((r, c) => tableData[`${r}-${c}`] || '', [tableData]);

  const setCell = useCallback((r, c, val) => {
    setTableData(prev => ({ ...prev, [`${r}-${c}`]: val }));
  }, []);

  const updateDOMSelection = useCallback((startCell, endCell) => {
    const sel = window.getSelection();
    sel.removeAllRanges();
    if (!startCell || !endCell) return;
    const startKey = `${startCell.row}-${startCell.col}`;
    const endKey = `${endCell.row}-${endCell.col}`;
    const startNode = cellRefs.current[startKey];
    const endNode = cellRefs.current[endKey];
    if (!startNode || !endNode) return;
    const range = document.createRange();
    range.setStart(startNode, 0);
    range.setEnd(endNode, endNode.childNodes.length || 0);
    sel.addRange(range);
  }, []);

  const handleCellMouseDown = useCallback((e, row, col) => {
    e.preventDefault();
    if (e.button !== 0) return;
    selectingRef.current = true;
    const cell = createCell(row, col);
    setSelection(prev => {
      const next = { ...prev, startCell: cell, endCell: cell, isSelecting: true };
      updateRange(next);
      return next;
    });
    setActiveCell({ row, col });
    updateDOMSelection(cell, cell);
  }, [updateDOMSelection]);

  const handleCellMouseEnter = useCallback((row, col) => {
    if (!selectingRef.current) return;
    const cell = createCell(row, col);
    setSelection(prev => {
      const next = { ...prev, endCell: cell, isSelecting: true };
      updateRange(next);
      return next;
    });
    const current = selectionRef.current;
    if (current.startCell) {
      updateDOMSelection(current.startCell, cell);
    }
  }, [updateDOMSelection]);

  const handleMouseUp = useCallback(() => {
    if (!selectingRef.current) return;
    selectingRef.current = false;
    setSelection(prev => ({ ...prev, isSelecting: false }));
  }, []);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseUp]);

  const handleCellChange = useCallback((row, col, value) => {
    setCell(row, col, value);
  }, [setCell]);

  const addRow = useCallback(() => {
    const newRow = rows;
    setRows(r => r + 1);
    setTableData(prev => {
      const next = { ...prev };
      for (let c = 0; c < cols; c++) {
        next[`${newRow}-${c}`] = '';
      }
      return next;
    });
  }, [rows, cols]);

  const addCol = useCallback(() => {
    const newCol = cols;
    setCols(c => c + 1);
    setTableData(prev => {
      const next = { ...prev };
      for (let r = 0; r < rows; r++) {
        next[`${r}-${newCol}`] = '';
      }
      return next;
    });
  }, [rows, cols]);

  const deleteRow = useCallback(() => {
    if (rows <= 1) return;
    const lastRow = rows - 1;
    setRows(r => r - 1);
    setTableData(prev => {
      const next = { ...prev };
      for (let c = 0; c < cols; c++) {
        delete next[`${lastRow}-${c}`];
      }
      return next;
    });
  }, [rows, cols]);

  const deleteCol = useCallback(() => {
    if (cols <= 1) return;
    const lastCol = cols - 1;
    setCols(c => c - 1);
    setTableData(prev => {
      const next = { ...prev };
      for (let r = 0; r < rows; r++) {
        delete next[`${r}-${lastCol}`];
      }
      return next;
    });
  }, [rows, cols]);

  const handleCopy = useCallback((e) => {
    const range = selectionRef.current.activeRange;
    const text = copyToClipboard(null, range, (r, c) => getCell(r, c));
    if (text) {
      e.clipboardData.setData('text/plain', text);
      e.preventDefault();
    }
  }, [getCell]);

  const handlePaste = useCallback((e) => {
    if (!activeCell) return;
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    const parsed = parseClipboardData(text);
    if (!parsed.length) return;
    let startRow = activeCell.row;
    let startCol = activeCell.col;
    setTableData(prev => {
      const next = { ...prev };
      for (let r = 0; r < parsed.length; r++) {
        for (let c = 0; c < parsed[r].length; c++) {
          const targetRow = startRow + r;
          const targetCol = startCol + c;
          if (targetRow < rows && targetCol < cols) {
            next[`${targetRow}-${targetCol}`] = parsed[r][c];
          }
        }
      }
      return next;
    });
  }, [activeCell, rows, cols]);

  useEffect(() => {
    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
    };
  }, [handleCopy, handlePaste]);

  const activeRange = selection.activeRange;

  const renderedCells = useMemo(() => {
    const cells = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const sel = isSelected({ row: r, col: c }, activeRange);
        const isActive = activeCell && activeCell.row === r && activeCell.col === c;
        cells.push(
          <td
            key={`${r}-${c}`}
            ref={el => { cellRefs.current[`${r}-${c}`] = el; }}
            className={`word-table-cell${sel ? ' selected' : ''}${isActive ? ' active' : ''}`}
            onMouseDown={(e) => handleCellMouseDown(e, r, c)}
            onMouseEnter={() => handleCellMouseEnter(r, c)}
            style={{
              border: '1px solid #999',
              padding: 0,
              minWidth: 100,
              height: 32,
              position: 'relative',
              background: sel ? 'rgba(0, 120, 215, 0.25)' : isActive ? 'rgba(0, 120, 215, 0.08)' : 'white',
              borderColor: isActive ? '#0078d7' : sel ? '#0078d7' : '#999',
              borderWidth: isActive || sel ? 2 : 1
            }}
          >
            <input
              className="word-table-input"
              value={getCell(r, c)}
              onChange={(e) => handleCellChange(r, c, e.target.value)}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                outline: 'none',
                padding: '2px 6px',
                fontSize: 13,
                fontFamily: 'Calibri, Arial, sans-serif',
                background: 'transparent',
                boxSizing: 'border-box'
              }}
            />
          </td>
        );
      }
    }
    return cells;
  }, [rows, cols, activeRange, activeCell, getCell, handleCellMouseDown, handleCellMouseEnter, handleCellChange]);

  const rowsArray = useMemo(() => {
    const result = [];
    for (let r = 0; r < rows; r++) {
      const rowCells = renderedCells.slice(r * cols, (r + 1) * cols);
      result.push(<tr key={r}>{rowCells}</tr>);
    }
    return result;
  }, [renderedCells, rows, cols]);

  return (
    <div style={{ fontFamily: 'Calibri, Arial, sans-serif', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{
        padding: '8px 16px',
        background: '#f3f3f3',
        borderBottom: '1px solid #d0d0d0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 8
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>Igitabo Word - Table Editor</span>
          <span style={{ fontSize: 12, color: '#666' }}>
            {activeRange ? `Range: ${activeRange.startRow + 1},${activeRange.startCol + 1} → ${activeRange.endRow + 1},${activeRange.endCol + 1}` : 'No selection'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button className="word-toolbar-btn" onClick={addRow} title="Add row">+ Row</button>
          <button className="word-toolbar-btn" onClick={addCol} title="Add column">+ Col</button>
          <button className="word-toolbar-btn word-toolbar-btn-danger" onClick={deleteRow} title="Delete last row">- Row</button>
          <button className="word-toolbar-btn word-toolbar-btn-danger" onClick={deleteCol} title="Delete last column">- Col</button>
        </div>
      </div>

      <div style={{
        flex: 1,
        overflow: 'auto',
        padding: 24,
        background: '#e8e8e8',
        display: 'flex',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'white',
          boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
          padding: 40,
          minWidth: 600,
          minHeight: 400
        }}>
          <table
            ref={tableRef}
            style={{
              borderCollapse: 'collapse',
              width: '100%',
              tableLayout: 'fixed'
            }}
          >
            <tbody>{rowsArray}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default WordTableEditor;
